/**
 * RaceSegmentCharts — Velocity, Stroke Rate, DPS per segment
 *
 * Renders three chart blocks modeled after the Minas TC analysis system:
 *   1. Velocity (m/s) — bar chart for all 5 segments
 *   2. Stroke Rate (str/min) — bar chart for 3 mid segments (15-45m)
 *   3. Distance per Stroke (m/str) — bar chart for 3 mid segments
 *
 * Also shows aggregate metrics + pacing info.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { Activity, Zap, Ruler, AlertTriangle, ChevronDown } from 'lucide-react';
import { getSegmentCharts } from '../../services/raceAnalysisService';

// ── Theme ──
const CHART_COLORS = {
  velocity: '#0ea5e9',      // sky-500
  velocityMax: '#0284c7',   // sky-600
  strokeRate: '#f59e0b',    // amber-500
  dps: '#10b981',           // emerald-500
  muted: '#cbd5e1',         // slate-300
};

const SEGMENT_LABELS_PT = {
  start_breakout: 'Saída',
  build: 'Construção',
  mid_pool: 'Meio',
  hold: 'Manutenção',
  finish: 'Chegada',
};

// ── Custom Tooltip ──
const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 text-sm">
      <p className="font-semibold text-gray-900">{label}</p>
      <p className="text-gray-600">
        {val?.toFixed(2)} <span className="text-gray-400">{unit}</span>
      </p>
    </div>
  );
};

// ── Metric Card ──
const MetricCard = ({ icon: Icon, label, value, unit, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: `${color}20` }}
    >
      <Icon size={20} style={{ color }} />
    </div>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900">
        {value ?? '—'} <span className="text-xs font-normal text-gray-400">{unit}</span>
      </p>
    </div>
  </div>
);

// ── Single Chart Block ──
const SegmentChart = ({ title, subtitle, icon: Icon, color, data, unit, yDomain }) => {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.value));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} style={{ color }} />
        <div>
          <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            domain={yDomain || ['auto', 'auto']}
            width={45}
          />
          <Tooltip content={<ChartTooltip unit={unit} />} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={48}>
            {data.map((entry, idx) => (
              <Cell
                key={idx}
                fill={entry.value === maxVal ? color : `${color}80`}
              />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              formatter={(v) => v?.toFixed(2)}
              style={{ fontSize: 10, fill: '#6b7280' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Main Component ──
const RaceSegmentCharts = ({ athleteId, style }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRaceId, setSelectedRaceId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSegmentCharts(athleteId, {
        raceId: selectedRaceId || undefined,
        style,
      });
      setChartData(result);
    } catch (err) {
      setError('Erro ao carregar dados da análise');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [athleteId, selectedRaceId, style]);

  useEffect(() => {
    if (athleteId) fetchData();
  }, [athleteId, fetchData]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl h-64 animate-pulse border border-gray-200" />
        ))}
      </div>
    );
  }

  // ── No data ──
  if (!chartData) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
        <Activity size={48} className="mx-auto mb-4 text-gray-300" />
        <h3 className="font-bold text-lg text-gray-900 mb-2">Análise Biomecânica</h3>
        <p className="text-gray-500 text-sm mb-4">
          Nenhuma análise de corrida encontrada para este atleta.
        </p>
        <p className="text-gray-400 text-xs">
          Faça upload de um vídeo de corrida na plataforma para gerar métricas por segmento.
        </p>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
        <AlertTriangle size={32} className="mx-auto mb-2 text-red-400" />
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  const { velocity, stroke_rate, dps, race_info, all_races } = chartData;

  // Build display data with Portuguese labels
  const velocityDisplay = (velocity || []).map(v => ({
    ...v,
    label: `${v.label}\n${SEGMENT_LABELS_PT[v.segment] || v.segment}`,
  }));

  const srDisplay = (stroke_rate || []).map(v => ({
    ...v,
    label: `${v.label}\n${SEGMENT_LABELS_PT[v.segment] || v.segment}`,
  }));

  const dpsDisplay = (dps || []).map(v => ({
    ...v,
    label: `${v.label}\n${SEGMENT_LABELS_PT[v.segment] || v.segment}`,
  }));

  return (
    <div className="space-y-6">
      {/* ── Header: Race info + selector ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Activity size={18} className="text-sky-500" />
              Análise por Segmento
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {race_info?.event_name}
              {race_info?.competition_name && ` — ${race_info.competition_name}`}
              {race_info?.official_time_s && (
                <span className="ml-2 font-semibold text-gray-700">
                  {race_info.official_time_s.toFixed(2)}s
                </span>
              )}
            </p>
            {!race_info?.calibrated && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertTriangle size={12} />
                Métricas estimadas (sem tempo oficial para calibração)
              </p>
            )}
          </div>

          {/* Race selector */}
          {all_races && all_races.length > 1 && (
            <div className="flex-shrink-0">
              <label className="text-xs text-gray-500 block mb-1">Corrida</label>
              <div className="relative">
                <select
                  value={selectedRaceId || race_info?.race_id || ''}
                  onChange={(e) => setSelectedRaceId(parseInt(e.target.value))}
                  className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm"
                >
                  {all_races.map(r => (
                    <option key={r.race_id} value={r.race_id}>
                      {r.event_name}
                      {r.official_time_s ? ` — ${r.official_time_s.toFixed(2)}s` : ''}
                      {r.competition_date ? ` (${new Date(r.competition_date).toLocaleDateString('pt-BR')})` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}
        </div>

        {/* Aggregate metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <MetricCard
            icon={Zap}
            label="Velocidade Média"
            value={race_info?.avg_velocity_ms?.toFixed(2)}
            unit="m/s"
            color={CHART_COLORS.velocity}
          />
          <MetricCard
            icon={Activity}
            label="Freq. Braçada"
            value={race_info?.avg_stroke_rate?.toFixed(1)}
            unit="str/min"
            color={CHART_COLORS.strokeRate}
          />
          <MetricCard
            icon={Ruler}
            label="Dist. por Braçada"
            value={race_info?.avg_dps?.toFixed(2)}
            unit="m/str"
            color={CHART_COLORS.dps}
          />
          <MetricCard
            icon={Activity}
            label="Pacing"
            value={
              race_info?.pacing_type === 'negative_split' ? 'Negativo ✓'
              : race_info?.pacing_type === 'positive_split' ? 'Positivo'
              : race_info?.pacing_type === 'even' ? 'Equilibrado'
              : '—'
            }
            unit=""
            color="#6366f1"
          />
        </div>
      </div>

      {/* ── Chart 1: Velocity ── */}
      <SegmentChart
        title="Velocidade por Segmento"
        subtitle="Variação da velocidade em 5 trechos da piscina (m/s)"
        icon={Zap}
        color={CHART_COLORS.velocity}
        data={velocityDisplay}
        unit="m/s"
      />

      {/* ── Chart 2: Stroke Rate ── */}
      <SegmentChart
        title="Frequência de Braçada"
        subtitle="Braçadas por minuto nos trechos de nado livre (15-45m)"
        icon={Activity}
        color={CHART_COLORS.strokeRate}
        data={srDisplay}
        unit="str/min"
      />

      {/* ── Chart 3: Distance per Stroke ── */}
      <SegmentChart
        title="Distância por Braçada"
        subtitle="Metros percorridos por braçada nos trechos intermediários (15-45m)"
        icon={Ruler}
        color={CHART_COLORS.dps}
        data={dpsDisplay}
        unit="m/str"
      />

      {/* ── Fatigue indicator ── */}
      {race_info?.fatigue_index != null && race_info.fatigue_index > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-amber-500" />
            <span className="font-bold text-sm text-amber-700">Índice de Fadiga</span>
          </div>
          <p className="text-sm text-amber-600">
            Queda de {race_info.fatigue_index.toFixed(1)}% na velocidade do primeiro para o último segmento.
            {race_info.fatigue_index > 10 && ' Considere ajustar a estratégia de pacing.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default RaceSegmentCharts;
